import React, { useState } from 'react';
import { Globe, Facebook, Twitter, Linkedin, Instagram, Youtube, Github } from 'lucide-react';

interface SocialLink {
  platform: string;
  url: string;
  label: string;
}

interface SocialLinksProps {
  label: string;
  value: SocialLink[];
  onSave: (links: SocialLink[]) => Promise<void>;
  disabled?: boolean;
}

const SOCIAL_PLATFORMS = [
  { value: 'website', label: 'Site Web', icon: Globe, placeholder: 'https://exemple.com' },
  { value: 'facebook', label: 'Facebook', icon: Facebook, placeholder: 'https://facebook.com/entreprise' },
  { value: 'twitter', label: 'Twitter/X', icon: Twitter, placeholder: 'https://twitter.com/entreprise' },
  { value: 'linkedin', label: 'LinkedIn', icon: Linkedin, placeholder: 'https://linkedin.com/company/entreprise' },
  { value: 'instagram', label: 'Instagram', icon: Instagram, placeholder: 'https://instagram.com/entreprise' },
  { value: 'youtube', label: 'YouTube', icon: Youtube, placeholder: 'https://youtube.com/@entreprise' },
  { value: 'github', label: 'GitHub', icon: Github, placeholder: 'https://github.com/entreprise' },
] as const;

export const SocialLinksInput: React.FC<SocialLinksProps> = ({
  label,
  value = [],
  onSave,
  disabled = false
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempLinks, setTempLinks] = useState<SocialLink[]>(value);

  const startEditing = () => {
    if (disabled) return;
    setIsEditing(true);
    setTempLinks([...value]);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setTempLinks([...value]);
  };

  const saveChanges = async () => {
    const validLinks = tempLinks.filter(link => link.url.trim() !== '');
    await onSave(validLinks);
    setIsEditing(false);
  };

  const updateLink = (platform: string, url: string) => {
    const platformInfo = SOCIAL_PLATFORMS.find(p => p.value === platform);
    if (!platformInfo) return;

    const existingIndex = tempLinks.findIndex(link => link.platform === platform);
    
    if (existingIndex >= 0) {
      if (url.trim() === '') {
        // Supprimer le lien si l'URL est vide
        setTempLinks(tempLinks.filter((_, index) => index !== existingIndex));
      } else {
        // Mettre à jour le lien existant
        const updatedLinks = [...tempLinks];
        updatedLinks[existingIndex] = {
          platform,
          url: url.trim(),
          label: platformInfo.label
        };
        setTempLinks(updatedLinks);
      }
    } else if (url.trim() !== '') {
      // Ajouter un nouveau lien
      setTempLinks([...tempLinks, {
        platform,
        url: url.trim(),
        label: platformInfo.label
      }]);
    }
  };

  const getLinkValue = (platform: string) => {
    return tempLinks.find(link => link.platform === platform)?.url || '';
  };

  const getDisplayLinks = () => {
    return value.filter(link => link.url.trim() !== '');
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        {label}
      </label>

      {!isEditing ? (
        // Mode affichage
        <div
          onClick={startEditing}
          className={`
            min-h-[2.5rem] p-3 border border-gray-300 rounded-lg
            ${disabled ? 'bg-gray-50 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50'}
            transition-colors duration-200
          `}
        >
          {getDisplayLinks().length > 0 ? (
            <div className="space-y-2">
              {getDisplayLinks().map((link, index) => {
                const platformInfo = SOCIAL_PLATFORMS.find(p => p.value === link.platform);
                const IconComponent = platformInfo?.icon || Globe;
                
                return (
                  <div key={index} className="flex items-center space-x-2">
                    <IconComponent className="w-4 h-4 text-gray-500" />
                    <span className="text-sm font-medium text-gray-700">{link.label}:</span>
                    <a
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-[#197ca8] hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {link.url}
                    </a>
                  </div>
                );
              })}
            </div>
          ) : (
            <span className="text-gray-400 text-sm">Cliquez pour ajouter des liens</span>
          )}
        </div>
      ) : (
        // Mode édition
        <div className="space-y-3 border border-gray-300 rounded-lg p-4">
          {SOCIAL_PLATFORMS.map((platform) => {
            const IconComponent = platform.icon;
            
            return (
              <div key={platform.value} className="space-y-1">
                <div className="flex items-center space-x-2">
                  <IconComponent className="w-4 h-4 text-gray-500" />
                  <label className="text-sm font-medium text-gray-700">
                    {platform.label}
                  </label>
                </div>
                <input
                  type="url"
                  value={getLinkValue(platform.value)}
                  onChange={(e) => updateLink(platform.value, e.target.value)}
                  placeholder={platform.placeholder}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-[#197ca8] focus:border-[#197ca8]"
                />
              </div>
            );
          })}

          {/* Boutons d'action */}
          <div className="flex justify-end space-x-2 pt-2 border-t border-gray-200">
            <button
              type="button"
              onClick={cancelEditing}
              className="px-3 py-2 text-sm border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={saveChanges}
              className="px-3 py-2 text-sm bg-[#197ca8] text-white rounded hover:bg-[#156a92]"
            >
              Sauvegarder
            </button>
          </div>
        </div>
      )}
    </div>
  );
};